/**
 * Nommer les jours de la semaine qui vient.
 *
 * Sur un horizon de sept jours, le nom du jour suffit et se lit plus vite qu'une date : on sait
 * ce que « samedi » veut dire sans compter. Au-delà, il redeviendrait ambigu — mais au-delà, on
 * ne montre rien ici.
 */

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'] as const

/** Le même jour calendaire, au fuseau de l'appareil. */
function memeJour(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/**
 * Le nom à afficher pour un rendez-vous à venir.
 *
 * @param quand le début du rendez-vous.
 * @param aujourdhui l'instant de référence — passé en paramètre pour que le test ne dépende
 *   pas du jour où il s'exécute.
 */
export function libelleDuJour(quand: Date, aujourdhui: Date): string {
  const demain = new Date(aujourdhui)
  demain.setDate(demain.getDate() + 1)

  if (memeJour(quand, demain)) {
    return 'Demain'
  }

  const nom = JOURS[quand.getDay()] ?? ''

  return nom.charAt(0).toUpperCase() + nom.slice(1)
}

/** Un jour et ce qu'il porte, dans l'ordre. */
export type JourGroupe<T> = { readonly libelle: string; readonly elements: readonly T[] }

/**
 * Regroupe des rendez-vous par jour, en conservant leur ordre.
 *
 * Le regroupement est fait ici et non à l'affichage : une liste plate de sept rendez-vous sans
 * repère de jour se lit mal, et répéter le nom du jour sur chaque ligne le dirait trois fois.
 */
export function parJour<T>(
  elements: readonly T[],
  quand: (element: T) => Date,
  aujourdhui: Date,
): readonly JourGroupe<T>[] {
  const groupes: JourGroupe<T>[] = []

  for (const element of elements) {
    const libelle = libelleDuJour(quand(element), aujourdhui)
    const dernier = groupes[groupes.length - 1]

    if (dernier && dernier.libelle === libelle) {
      groupes[groupes.length - 1] = {
        libelle,
        elements: [...dernier.elements, element],
      }
    } else {
      groupes.push({ libelle, elements: [element] })
    }
  }

  return groupes
}
